import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SourcingChannelStatus } from 'api-clients/monolith';
import { differenceInCalendarDays } from 'date-fns';
import { useIntl } from 'react-intl';

import { updateCampaignStatus } from '@/api-clients/update-campaign-status';
import { useConfirmation } from '@/components/base/confirmation';
import { useToaster } from '@/components/base/toaster';
import { SetReachPublisherStatus } from '@/components/pages/channels/campaigns/use-reach-channels';
import { usePostHog } from '@/hooks/use-posthog';
import { queryKeys } from '@/query-keys';
import { POOL, REACH } from '@/types/channels';

import { useSetCampaignStatus } from './use-set-campaign-status';

function useMessages(isCampaignActive: boolean, isPool: boolean) {
  const intl = useIntl();

  if (isPool && isCampaignActive) {
    return {
      confirmTitle: intl.formatMessage({
        defaultMessage: 'Complete Pool?',
        description:
          'Dialog title: confirm ending pool sourcing for this opening',
      }),
      confirmDescription: intl.formatMessage({
        defaultMessage:
          'Marking this pool as complete will stop sourcing from this pool for this opening. Note: This action cannot be undone. The pool will move to a completed state.',
        description:
          'Dialog body: irreversible; stops pool sourcing and marks the pool completed',
      }),
      confirmButtonLabel: intl.formatMessage({
        defaultMessage: 'Complete pool',
        description: 'Primary button to confirm completing a pool',
      }),
      notifySuccessMessage: intl.formatMessage({
        defaultMessage: 'Pool completed successfully!',
        description: 'Success toast after pool is marked complete',
      }),
      notifyErrorMessage: intl.formatMessage({
        defaultMessage: 'Failed to complete pool!',
        description: 'Error toast when complete-pool request fails',
      }),
    };
  }

  if (isCampaignActive) {
    return {
      confirmTitle: intl.formatMessage({
        defaultMessage: 'Pause campaign',
        description: 'Dialog title: confirm pausing an active channel campaign',
      }),
      confirmDescription: intl.formatMessage({
        defaultMessage: 'Are you sure you want to pause this campaign?',
        description: 'Dialog body asking user to confirm pausing a campaign',
      }),
      confirmButtonLabel: intl.formatMessage({
        defaultMessage: 'Yes, Pause campaign',
        description: 'Primary button to confirm pausing a campaign',
      }),
      notifySuccessMessage: intl.formatMessage({
        defaultMessage: 'Campaign paused successfully!',
        description: 'Success toast after campaign is paused',
      }),
      notifyErrorMessage: intl.formatMessage({
        defaultMessage: 'Failed to pause campaign!',
        description: 'Error toast when pause request fails',
      }),
    };
  }

  return {
    confirmTitle: intl.formatMessage({
      defaultMessage: 'Resume campaign',
      description: 'Dialog title: confirm resuming a paused channel campaign',
    }),
    confirmDescription: intl.formatMessage({
      defaultMessage: 'Are you sure you want to resume this campaign?',
      description: 'Dialog body asking user to confirm resuming a campaign',
    }),
    confirmButtonLabel: intl.formatMessage({
      defaultMessage: 'Yes, resume campaign',
      description: 'Primary button to confirm resuming a campaign',
    }),
    notifySuccessMessage: intl.formatMessage({
      defaultMessage: 'Campaign resumed successfully!',
      description: 'Success toast after campaign is resumed',
    }),
    notifyErrorMessage: intl.formatMessage({
      defaultMessage: 'Failed to resume campaign!',
      description: 'Error toast when resume request fails',
    }),
  };
}

interface UseToggleCampaignOptions {
  sourcingPurchaseId: string;
  reachCampaignId: string | null;
  status: SourcingChannelStatus;
  channel: string;
  setReachPublisherStatus?: SetReachPublisherStatus;
  // Only the Campaigns page passes these. Other callers, such as the Channels
  // page, send pause and resume without campaign-level detail.
  openingName?: string;
  openingId?: string;
  startDate?: string | null;
  endDate?: string | null;
  budget?: number | null;
  spent?: number | null;
}

export function useToggleCampaign({
  sourcingPurchaseId,
  reachCampaignId,
  status,
  channel,
  setReachPublisherStatus,
  openingName,
  openingId,
  startDate,
  endDate,
  budget,
  spent,
}: UseToggleCampaignOptions) {
  const queryClient = useQueryClient();
  const setCampaignStatus = useSetCampaignStatus();
  const { notifySuccess, notifyError } = useToaster();
  const { confirm } = useConfirmation();
  const posthog = usePostHog();

  const isCampaignActive = status === 'active';
  const isPool = channel === POOL;
  const updatedStatus: SourcingChannelStatus = isCampaignActive
    ? 'stopped'
    : 'active';

  const {
    confirmTitle,
    confirmDescription,
    confirmButtonLabel,
    notifySuccessMessage,
    notifyErrorMessage,
  } = useMessages(isCampaignActive, isPool);

  const { mutate, isPending } = useMutation({
    mutationFn: updateCampaignStatus,
    onSuccess: async () => {
      notifySuccess({ message: notifySuccessMessage });

      const sharedProperties = {
        campaign_id: sourcingPurchaseId,
        opening_name: openingName,
        opening_id: openingId,
        channel,
        budget,
      };
      // Completing a Pool ends it for good; it is no pause, and no resume
      // can follow it.
      if (isCampaignActive && !isPool) {
        // isCampaignActive still reflects the pre-toggle status here — a
        // pause is happening.
        posthog.capture('source_campaign:status_pause', {
          ...sharedProperties,
          days_since_campaign_launch: startDate
            ? differenceInCalendarDays(new Date(), new Date(startDate))
            : undefined,
          spent,
          paused_via: 'manual',
        });
      } else if (!isCampaignActive) {
        // days_paused is omitted — the campaign model has no paused-at
        // timestamp to diff against.
        posthog.capture('source_campaign:status_resume', {
          ...sharedProperties,
          days_left: endDate
            ? differenceInCalendarDays(new Date(endDate), new Date())
            : undefined,
          resumed_via: 'manual',
        });
      }

      if (reachCampaignId && setReachPublisherStatus) {
        setReachPublisherStatus(
          sourcingPurchaseId,
          reachCampaignId,
          updatedStatus,
        );
      } else {
        setCampaignStatus(sourcingPurchaseId, updatedStatus);
      }
      if (channel === REACH) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.openingDetails.channels.reach,
        });
      }
    },
    onError: () => {
      notifyError({ message: notifyErrorMessage });
    },
  });

  const handleToggle = async () => {
    const confirmed = await confirm({
      title: confirmTitle,
      description: confirmDescription,
      confirmLabel: confirmButtonLabel,
    });
    if (confirmed) {
      mutate({
        sourcingPurchaseId,
        requestBody: {
          enabled: !isCampaignActive,
          channel,
          reach_campaign_id: reachCampaignId,
        },
      });
    }
  };

  return { handleToggle, isPending, isCampaignActive, isPool };
}
